import type { EChartsOption, GaugeSeriesOption } from 'echarts';

import { toBaseOption, toTooltipStyle } from './shared';

// How thick the arc is, as a share of the gauge's radius
const ARC_WIDTH = 0.4;
// How much of the room the gauge's radius takes: its ends' labels sit
// beside it
const RADIUS_SHARE = 0.8;
// How far down the half circle's centre sits
const CENTRE = 0.72;
// Used until the chart knows its size, e.g. in tests
const FALLBACK_SIZE = { width: 400, height: 300 };
// The ends' labels: a little larger than axis labels, below the arc
const END_LABEL_SIZE = 12;
const END_LABEL_GAP = 6;

// A gauge, as the Highcharts one: a half circle, filled from the left as far
// as the value's percentile among its peers, in a colour from danger to
// success; the value in its middle, the peer group's worst and best values
// at its ends
const toGaugeOption = ({
  model,
  theme,
  size = FALLBACK_SIZE,
}: {
  model: StudioCharts.GaugeModel;
  theme: StudioCharts.Theme;
  // The room it has: a gauge sizes its arc in pixels, not by its radius
  size?: { width: number; height: number };
}): EChartsOption => {
  // Its radius: within half the width, or (a half circle) as far as its
  // centre sits down the height
  const radius = Math.max(
    0,
    Math.min(size.width / 2, size.height * CENTRE) * RADIUS_SHARE,
  );
  const arcWidth = Math.round(radius * ARC_WIDTH);
  const series: GaugeSeriesOption = {
    type: 'gauge',
    id: model.seriesId,
    name: model.name,
    startAngle: 180,
    endAngle: 0,
    min: 0,
    max: 100,
    center: ['50%', `${CENTRE * 100}%`],
    radius,
    data: [{ value: model.fill, name: model.name }],
    progress: {
      show: true,
      width: arcWidth,
      itemStyle: { color: model.color },
    },
    // The arc it hasn't filled
    axisLine: {
      lineStyle: { width: arcWidth, color: [[1, theme.gaugeTrack]] },
    },
    pointer: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    splitNumber: 1,
    // Its ends are labelled below the arc instead (see endLabels)
    axisLabel: { show: false },
    title: { show: false },
    detail: {
      valueAnimation: false,
      offsetCenter: [0, '-12%'],
      color: theme.textPrimary,
      fontSize: 25,
      formatter: () => model.valueLabel,
    },
  };
  // The peer group's worst and best values, each centred under its end of
  // the arc, clear of it
  const centreX = size.width / 2;
  const top = size.height * CENTRE + END_LABEL_GAP;
  const endLabel = ({
    id,
    text,
    x,
  }: {
    id: string;
    text: string;
    x: number;
  }) => ({
    type: 'text' as const,
    id,
    left: x,
    top,
    style: {
      text,
      x: 0,
      y: 0,
      align: 'center' as const,
      fill: theme.textPrimary,
      fontSize: END_LABEL_SIZE,
      fontFamily: theme.fontFamily,
    },
    // Centred on x, not starting at it
    bounding: 'raw' as const,
  });
  const endOffset = radius - arcWidth / 2;
  return {
    ...toBaseOption(theme),
    graphic: [
      endLabel({
        id: 'gauge-start',
        text: model.startLabel,
        x: centreX - endOffset,
      }),
      endLabel({
        id: 'gauge-end',
        text: model.endLabel,
        x: centreX + endOffset,
      }),
    ],
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: () =>
        model.entityLabel
          ? `${model.entityLabel}: ${model.valueLabel}`
          : model.valueLabel,
    },
    series: [series],
  };
};

export default toGaugeOption;
